import * as XLSX from "xlsx";
import { EXPECTED_COLUMNS } from "../constants";
import { normalizeSalesImportBranchId } from "../config/salesImportBranchIds";
import {
  dmyStringToYmd,
  excelSerialToYmd,
  normalizeTranDateString,
} from "../utils/dateParse";

function normHeader(h) {
  return String(h ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, "");
}

const HEADER_MAP = {};
for (const col of EXPECTED_COLUMNS) {
  HEADER_MAP[normHeader(col)] = col;
}

/** Map normalized header → canonical field key on row object */
const FIELD_KEYS = {
  vouchertype: "voucherType",
  voucherno: "voucherNo",
  mvoucherno: "mVoucherNo",
  billno: "billNo",
  trandate: "tranDate",
  transdate: "tranDate",
  customername: "customerName",
  employeename: "employeeName",
  namestaffname: "staffName",
  staffname: "staffName",
  itemgroup: "itemGroup",
  brandname: "brandName",
  itemname: "itemName",
  alias: "alias",
  partno: "partNo",
  productid: "productId",
  producth: "productId",
  qty: "qty",
  unitprice: "unitPrice",
  mrp: "mrp",
  discount: "discount",
  discountamt: "discountAmt",
  discountam: "discountAmt",
  amount: "amount",
  billamount: "billAmount",
  profitcentre: "profitCentre",
  profitpercent: "profitPercent",
  workstationid: "workstationId",
  branchid: "branchId",
};

/** SQL export headers not in EXPECTED_COLUMNS (spaces / naming variants). */
const HEADER_ALIASES = {
  mvoucherno: "mVoucherNo",
  transdate: "tranDate",
  producth: "productId",
  discountam: "discountAmt",
  profitpercent: "profitPercent",
  ledgername: "customerName",
  partyname: "customerName",
  customer: "customerName",
  namestaffname: "staffName",
  staffname: "staffName",
  profitcentrename: "profitCentre",
  profitcentername: "profitCentre",
  profitcentre: "profitCentre",
  brand: "brandName",
  sl: null,
  slno: null,
  serialno: null,
};

const NUMERIC_FIELDS = new Set([
  "qty",
  "unitPrice",
  "mrp",
  "discount",
  "discountAmt",
  "amount",
  "billAmount",
]);

function resolveFieldKeyForHeader(label) {
  const n = normHeader(label);
  let fieldKey = HEADER_ALIASES[n];
  if (fieldKey === null) return null;
  if (!fieldKey) {
    const canon = HEADER_MAP[n];
    if (canon) fieldKey = FIELD_KEYS[normHeader(canon)] || FIELD_KEYS[n];
    else fieldKey = FIELD_KEYS[n];
  }
  return fieldKey || null;
}

function cellLabel(cell) {
  if (!cell) return "";
  if (cell.w != null && String(cell.w).trim() !== "") return String(cell.w).trim();
  if (cell.v == null) return "";
  if (cell.v instanceof Date) return "";
  return String(cell.v).trim();
}

/**
 * Use Excel display text / format for Trans Date (avoids serial −7 showing as previous week end).
 */
export function tranDateFromExcelCell(cell) {
  if (!cell) return "";

  const display = cellLabel(cell);
  if (display) {
    const fromDmy = dmyStringToYmd(display);
    if (fromDmy) return fromDmy;
    const iso = display.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  }

  if (cell.t === "n" && typeof cell.v === "number" && cell.z && XLSX.SSF?.format) {
    try {
      const formatted = XLSX.SSF.format(cell.z, cell.v);
      const fromFmt = dmyStringToYmd(formatted);
      if (fromFmt) return fromFmt;
    } catch {
      /* ignore SSF errors */
    }
  }

  if (typeof cell.v === "number" && cell.v > 20000 && cell.v < 120000) {
    return excelSerialToYmd(cell.v);
  }

  return normalizeTranDateString(cell.v ?? display);
}

function parseNumber(v) {
  if (v == null || v === "") return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  let s = String(v).trim();
  const neg = /^\(.*\)$/.test(s);
  if (neg) s = s.slice(1, -1);
  s = s.replace(/[₹$,\s]/g, "");
  if (s === "" || s === "-") return null;
  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  return neg ? -Math.abs(n) : n;
}

function cellToProductId(v) {
  if (v == null || v === "") return "";
  if (typeof v === "number" && Number.isFinite(v)) {
    if (Math.abs(v) >= 1e12) return v.toFixed(0);
    return String(Math.round(v));
  }
  return String(v).trim().replace(/[,\s]/g, "");
}

function cellToString(v) {
  if (v == null) return "";
  return String(v).trim();
}

function findHeaderRowIndex(sheet, range) {
  const maxScan = Math.min(range.s.r + 8, range.e.r);
  let bestRow = range.s.r;
  let bestHits = 0;
  for (let r = range.s.r; r <= maxScan; r += 1) {
    let hits = 0;
    for (let c = range.s.c; c <= range.e.c; c += 1) {
      const cell = sheet[XLSX.utils.encode_cell({ r, c })];
      if (resolveFieldKeyForHeader(cellLabel(cell))) hits += 1;
    }
    if (hits > bestHits) {
      bestHits = hits;
      bestRow = r;
    }
  }
  return bestRow;
}

function parseSheetRows(sheet) {
  const range = XLSX.utils.decode_range(sheet["!ref"] || "A1");
  const headerRow = findHeaderRowIndex(sheet, range);
  const colField = {};

  for (let c = range.s.c; c <= range.e.c; c += 1) {
    const cell = sheet[XLSX.utils.encode_cell({ r: headerRow, c })];
    const fk = resolveFieldKeyForHeader(cellLabel(cell));
    if (fk) colField[c] = fk;
  }

  const rows = [];
  for (let r = headerRow + 1; r <= range.e.r; r += 1) {
    const row = { _rowIndex: r + 1 };
    let hasData = false;

    for (let c = range.s.c; c <= range.e.c; c += 1) {
      const fieldKey = colField[c];
      if (!fieldKey) continue;

      const cell = sheet[XLSX.utils.encode_cell({ r, c })];
      if (!cell || (cell.v == null && (cell.w == null || cell.w === ""))) continue;
      hasData = true;

      if (fieldKey === "tranDate") {
        row.tranDate = tranDateFromExcelCell(cell);
      } else if (fieldKey === "branchId") {
        row.branchId = normalizeSalesImportBranchId(cell.v ?? cell.w);
      } else if (fieldKey === "productId") {
        row[fieldKey] = cellToProductId(cell.v ?? cell.w);
      } else if (NUMERIC_FIELDS.has(fieldKey)) {
        row[fieldKey] = parseNumber(cell.v ?? cell.w);
      } else {
        row[fieldKey] = cellToString(cell.v ?? cell.w);
      }
    }

    if (hasData) rows.push(row);
  }

  return rows;
}

/**
 * @param {ArrayBuffer} buffer
 * @returns {{ rows: object[], headers: string[], sheetName: string }}
 */
export function parseSalesExcelBuffer(buffer) {
  const wb = XLSX.read(buffer, { type: "array", cellDates: false });
  const sheetName = wb.SheetNames[0];
  if (!sheetName) {
    throw new Error("Workbook has no sheets.");
  }
  const sheet = wb.Sheets[sheetName];
  const rows = parseSheetRows(sheet);

  return { rows, headers: EXPECTED_COLUMNS, sheetName };
}

/** @param {File} file */
export async function parseSalesExcelFile(file) {
  const buffer = await file.arrayBuffer();
  return parseSalesExcelBuffer(buffer);
}

export function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes < 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}
